// utils/generateInvoicePDF.js
// Install dependency: npm install jspdf
// Usage: import { generateInvoicePDF } from '../utils/generateInvoicePDF';
//        generateInvoicePDF(plan);   // plan = one item from softwarePlans array

import jsPDF from "jspdf";

// ─── Color Palette ────────────────────────────────────────────────────────────
const C = {
  primary:    [15,  23,  42],   // slate-900  — page background header
  accent:     [37,  99, 235],   // blue-600
  accentLight:[219,234,254],   // blue-100
  green:      [22, 163,  74],   // green-600
  greenLight: [220,252,231],   // green-100
  gray900:    [17,  24,  39],
  gray600:    [75,  85,  99],
  gray400:   [156, 163,175],
  gray200:   [229,231,235],
  white:     [255,255,255],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
// eslint-disable-next-line
const rgb = (arr) => ({ r: arr[0], g: arr[1], b: arr[2] });

const setFill  = (doc, arr) => doc.setFillColor(...arr);
const setColor = (doc, arr) => doc.setTextColor(...arr);
const setDraw  = (doc, arr) => doc.setDrawColor(...arr);

const fmtDate = (iso) => {
  if (!iso) return "N/A";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const fmtCurrency = (val, currency = "INR") => {
  const symbol = currency === "INR" ? "Rs." : currency + " ";
  return symbol + Number(val || 0).toLocaleString("en-IN");
};

// ─── Main Generator ───────────────────────────────────────────────────────────
export function generateInvoicePDF(plan) {
  const inv  = plan.invoiceId || {};           // nested invoice object
  const doc  = new jsPDF({ unit: "mm", format: "a4" });

  const PW   = 210;   // page width  mm
  const PH   = 297;   // page height mm
  const ML   = 14;    // margin left
  const MR   = PW - ML;

  let y = 0;          // running cursor

  // ─── 1. Header Banner ──────────────────────────────────────────────────────
  setFill(doc, C.primary);
  doc.rect(0, 0, PW, 42, "F");

  // Company / app name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setColor(doc, C.white);
  doc.text("CICO Time", ML, 17);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setColor(doc, C.accentLight);
  doc.text("Attendance & Workforce Management", ML, 23);

  // "INVOICE" label on right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  setColor(doc, C.white);
  doc.text("INVOICE", MR, 17, { align: "right" });

  // Invoice number
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setColor(doc, C.accentLight);
  const invNum = inv.invoiceNumber || plan._id || "N/A";
  doc.text(`#${invNum}`, MR, 24, { align: "right" });

  y = 42;

  // ─── 2. Status + Date strip ────────────────────────────────────────────────
  setFill(doc, C.accentLight);
  doc.rect(0, y, PW, 10, "F");

  setColor(doc, C.accent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("PAYMENT STATUS", ML, y + 6.5);

  const paid = String(inv.paymentStatus).toLowerCase() === "true";
  setFill(doc, paid ? C.green : [239,68,68]);
  doc.roundedRect(ML + 36, y + 2, 18, 6, 1.5, 1.5, "F");
  setColor(doc, C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text(paid ? "PAID" : "UNPAID", ML + 45, y + 6.2, { align: "center" });

  // Date on right
  setColor(doc, C.gray600);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Date: ${fmtDate(inv.createdAt || plan.createdAt)}`, MR, y + 6.5, { align: "right" });

  y += 10;

  // ─── 3. Billed To + Invoice Info two-column block ─────────────────────────
  y += 8;

  // Left col — Billed To
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setColor(doc, C.accent);
  doc.text("BILLED TO", ML, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setColor(doc, C.gray900);
  doc.text(inv.orgname || plan.orgname || "N/A", ML, y);

  y += 5;
  const billedRows = [
    inv.email  || plan.email,
    inv.phone  || plan.phone,
    inv.address,
    inv.gst && inv.gst !== "NA" ? `GST: ${inv.gst}` : null,
  ].filter(Boolean);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setColor(doc, C.gray600);
  billedRows.forEach((line) => {
    doc.text(line, ML, y);
    y += 4.5;
  });

  // Right col — Invoice Details
  const rightX = PW / 2 + 10;
  let ry = y - billedRows.length * 4.5 - 5;   // align top with "Billed To"

  const invoiceDetails = [
    ["Invoice No",  invNum],
    ["Invoice Date",fmtDate(inv.createdAt || plan.createdAt)],
    ["Updated At",  fmtDate(inv.updatedAt  || plan.updatedAt)],
    ["Created By",  inv.createdby || "Admin"],
    ["Currency",    inv.currency  || "INR"],
    ["Plan Type",   plan.recipt   || "SoftwarePlan"],
  ];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setColor(doc, C.accent);
  doc.text("INVOICE DETAILS", rightX, ry);
  ry += 5;

  doc.setFontSize(8.5);
  invoiceDetails.forEach(([label, val]) => {
    setColor(doc, C.gray400);
    doc.setFont("helvetica", "normal");
    doc.text(label, rightX, ry);

    setColor(doc, C.gray900);
    doc.setFont("helvetica", "bold");
    doc.text(String(val), MR, ry, { align: "right" });
    ry += 5;
  });

  // Advance y to whichever column is longer
  y = Math.max(y, ry) + 6;

  // ─── 4. Divider ────────────────────────────────────────────────────────────
  setDraw(doc, C.gray200);
  doc.setLineWidth(0.3);
  doc.line(ML, y, MR, y);
  y += 8;

  // ─── 5. Line Items Table ───────────────────────────────────────────────────
  // Named column anchors — eliminates offset math that causes overlapping
  const nameStart      = ML;          // Name  left edge
  const planTypeStart  = ML + 72;     // Plan Type    left edge
  const qtyCenter      = ML + 118;    // Qty          center point
  const unitPriceRight = ML + 155;    // Unit Price   right edge (leaves gap before Total)
  const totalRight     = MR;          // Total        right edge

  setFill(doc, C.primary);
  doc.rect(ML - 2, y - 4, MR - ML + 4, 9, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setColor(doc, C.white);
  doc.text("Name", nameStart,      y + 1, { align: "left"   });
  doc.text("Plan Type",   planTypeStart,  y + 1, { align: "left"   });
  doc.text("Qty",         qtyCenter,      y + 1, { align: "center" });
  doc.text("Unit Price",  unitPriceRight, y + 1, { align: "right"  });
  doc.text("Total",       totalRight,     y + 1, { align: "right"  });
  y += 9;

  // Single line item from plan data
  const amount = inv.amount ?? plan.ammount ?? plan.amount ?? 0;
  const currency = inv.currency || "INR";

  const lineItems = [
    {
      name: inv.name || plan.name || "Software Plan Subscription",
      planType:    plan.recipt || "SoftwarePlan",
      qty:         1,
      unitPrice:   amount,
    },
  ];

  lineItems.forEach((item, idx) => {
    const rowY = y + idx * 10;
    if (idx % 2 === 0) {
      setFill(doc, [248, 250, 252]);
      doc.rect(ML - 2, rowY - 3.5, MR - ML + 4, 10, "F");
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    setColor(doc, C.gray900);
    doc.text(item.name,     nameStart,      rowY + 2, { align: "left"   });
    doc.text(item.planType,        planTypeStart,  rowY + 2, { align: "left"   });
    doc.text(String(item.qty),     qtyCenter,      rowY + 2, { align: "center" });

    setColor(doc, C.gray600);
    doc.text(fmtCurrency(item.unitPrice, currency), unitPriceRight, rowY + 2, { align: "right" });

    setColor(doc, C.green);
    doc.setFont("helvetica", "bold");
    doc.text(fmtCurrency(item.qty * item.unitPrice, currency), totalRight, rowY + 2, { align: "right" });
  });

  y += lineItems.length * 10 + 4;

  // ─── 6. Totals block ──────────────────────────────────────────────────────
  setDraw(doc, C.gray200);
  doc.setLineWidth(0.3);
  doc.line(ML, y, MR, y);
  y += 6;

  const totalsLeft = PW / 2 + 10;
  const totalRows = [
    ["Subtotal",  fmtCurrency(amount, currency)],
    ["GST / Tax", "Included"],
    ["Discount",  "—"],
  ];

  totalRows.forEach(([label, val]) => {
    setColor(doc, C.gray600);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(label, totalsLeft, y);

    setColor(doc, C.gray900);
    doc.setFont("helvetica", "bold");
    doc.text(val, MR, y, { align: "right" });
    y += 6;
  });

  y += 2;

  // Grand Total banner
  setFill(doc, C.primary);
  doc.rect(totalsLeft - 4, y - 4, MR - totalsLeft + 6, 12, "F");

  setColor(doc, C.accentLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("TOTAL AMOUNT", totalsLeft, y + 3.5);

  setColor(doc, C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(fmtCurrency(amount, currency), MR, y + 4, { align: "right" });

  y += 16;

  // ─── 7. Notes / Terms ─────────────────────────────────────────────────────
  setFill(doc, [248, 250, 252]);
  doc.rect(ML - 2, y, MR - ML + 4, 22, "F");
  setDraw(doc, C.gray200);
  doc.setLineWidth(0.2);
  doc.rect(ML - 2, y, MR - ML + 4, 22, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setColor(doc, C.accent);
  doc.text("NOTES & TERMS", ML, y + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setColor(doc, C.gray600);
  doc.text(
    "Thank you for your business! This invoice was generated automatically by CICO Admin.\nPayment is non-refundable as per our Terms of Service. For queries, contact sales@bandy-moot.com.",
    ML, y + 12,
    { maxWidth: MR - ML }
  );

  y += 28;

  // ─── 8. Footer ────────────────────────────────────────────────────────────
  setFill(doc, C.primary);
  doc.rect(0, PH - 18, PW, 18, "F");

  setColor(doc, C.accentLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("CICO Time  ·  Attendance & Workforce Management", PW / 2, PH - 10, { align: "center" });
  setColor(doc, C.gray400);
  doc.text("sales@bandy-moot.com  |  www.bandy-moot.com", PW / 2, PH - 5, { align: "center" });

  // ─── Save ─────────────────────────────────────────────────────────────────
  const filename = `CICOTime_Invoice_${invNum}.pdf`;
  doc.save(filename);
}
